<template>
  <LoginScreen v-if="!isAuthenticated && !isCheckingAuth" />

  <div v-show="isAuthenticated" class="app-layout">
    <AppSidebar />

    <main class="main-content">
      <router-view />

      <!-- 移动端专属备案信息底栏 -->
      <div class="mobile-footer">
        <a href="https://beian.miit.gov.cn/" target="_blank">ICP备案号：鄂ICP备2026015730号-1</a><br>
        <a href="https://beian.mps.gov.cn/#/query/webSearch?code=42050002420970" rel="noreferrer" target="_blank">
            <img src="/beian.png" width="11"/>
            鄂公网安备42050002420970号</a>
      </div>
    </main>
  </div>

  <!-- 登录过期遮罩层（只显示一次，不阻塞后续 401） -->
  <div v-if="sessionExpired" class="session-expired-overlay">
    <div class="session-expired-card">
      <div class="expired-icon">🔒</div>
      <h2>登录已过期</h2>
      <p>您的登录会话已过期，请重新登录以继续使用系统。</p>
      <button class="expired-btn" @click="goToLogin">重新登录</button>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '../composables/useAuth';
import LoginScreen from './LoginScreen.vue';
import AppSidebar from './AppSidebar.vue';

const { isAuthenticated, isCheckingAuth, sessionExpired, checkAuth, handleLogout } = useAuth();
const router = useRouter();

const goToLogin = () => {
  handleLogout();
  router.push('/');
};

onMounted(() => {
  checkAuth();
});
</script>

<style>
/* ========== 全局设计令牌 (CSS Variables) ========== */
:root {
  /* 色彩体系 */
  --color-primary: #3498db;
  --color-primary-dark: #2980b9;
  --color-primary-light: #5dade2;
  --color-danger: #e74c3c;
  --color-danger-dark: #c0392b;
  --color-success: #2ecc71;
  --color-warning: #f39c12;
  --color-warning-dark: #e67e22;
  --color-purple: #8e44ad;
  --color-purple-light: #764ba2;

  /* 中性色 */
  --color-text-primary: #2c3e50;
  --color-text-secondary: #7f8c8d;
  --color-text-muted: #95a5a6;
  --color-text-light: #bdc3c7;
  --color-border: #ecf0f1;
  --color-border-light: #f1f2f6;
  --color-bg-page: #f4f6f9;
  --color-bg-card: #ffffff;
  --color-bg-hover: #f8f9fa;
  --color-bg-sidebar: #2c3e50;

  /* 圆角体系 */
  --radius-sm: 6px;
  --radius-md: 12px;
  --radius-lg: 20px;

  /* 阴影体系 */
  --shadow-sm: 0 2px 8px rgba(0,0,0,0.04);
  --shadow-md: 0 4px 15px rgba(0,0,0,0.06);
  --shadow-lg: 0 10px 30px rgba(0,0,0,0.15);
  --shadow-modal: 0 10px 30px rgba(0,0,0,0.25);

  /* 过渡 */
  --transition-fast: 0.2s ease;
  --transition-normal: 0.3s ease;

  /* 布局 */
  --content-max-width: 1100px;
  --content-padding: 30px 40px;
  --modal-overlay: rgba(0, 0, 0, 0.6);
}

/* ========== 全局基础重置 ========== */
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
}

/* ========== 全局基础布局 ========== */
.app-layout {
  display: flex;
  width: 100vw;
  height: 100vh;
  background: white;
  overflow: hidden;
  font-family: sans-serif;
  text-align: left;
}

.main-content {
  flex: 1;
  padding: var(--content-padding);
  overflow-y: auto;
  background-color: var(--color-bg-page);
}

/* ========== 全局自定义滚动条 ========== */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(189, 195, 199, 0.5); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: rgba(189, 195, 199, 0.8); }

/* 备案底边栏默认隐藏（PC在侧边） */
.mobile-footer {
  display: none;
  text-align: center;
  margin-top: 35px;
  font-size: 12px;
  opacity: 0.5;
}
.mobile-footer a {
  color: var(--color-text-secondary);
  text-decoration: none;
}
.mobile-footer a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .app-layout {
    flex-direction: column;
  }

  .main-content {
    padding: 15px;
  }

  .mobile-footer {
    display: block;
  }

  .session-expired-card {
    width: 300px;
    max-width: calc(100vw - 32px);
    padding: 30px 24px;
    margin: 0 16px;
  }
}

/* 登录过期遮罩层 */
.session-expired-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.session-expired-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 36px;
  width: 360px;
  max-width: 85vw;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: fadeInUp 0.3s ease;
}
.expired-icon {
  font-size: 48px;
  margin-bottom: 12px;
}
.session-expired-card h2 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 22px;
}
.session-expired-card p {
  margin: 0 0 24px 0;
  color: #7f8c8d;
  font-size: 15px;
  line-height: 1.5;
}
.expired-btn {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  padding: 12px 36px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}
.expired-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(52,152,219,0.4);
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>